import React, { createContext, useState, useEffect } from 'react';
import { authAPI } from '../utils/api';

export const AuthContext = createContext({});

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  // Check if user is already logged in on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('auth_token');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Parse saved user from localStorage
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setIsLoggedIn(true);
          
          // Also verify token is still valid by fetching profile
          try {
            const response = await authAPI.getProfile();
            if (response.data.success === true) {
              const payload = response.data.data || {};
              const userObj = payload.user || payload;
              setUser(userObj);
            }
          } catch (profileError) {
            console.warn('[AuthContext] Profile verification failed, using cached user');
          }
        } catch (error) {
          // Token invalid, clear storage
          console.error('Token validation failed:', error);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          setUser(null);
          setIsLoggedIn(false);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const openLoginModal = () => setLoginModalOpen(true);
  const closeLoginModal = () => setLoginModalOpen(false);

  /**
   * Login with username/email and password
   * @param {Object} credentials - { username, password }
   * @returns {Promise}
   */
  const login = async (credentials) => {
    try {
      console.log('[AuthContext] Login attempt with:', { 
        username: credentials.username,
        hasPassword: !!credentials.password 
      });
      
      const response = await authAPI.login({
        username: credentials.username,
        password: credentials.password
      });

      console.log('[AuthContext] API response:', response.data);

      // API returns { success: true, message: '...', data: { user: {...}, token: '...' } }
      if (response.data.success === true) {
        const { token, user: userData } = response.data.data;
        
        console.log('[AuthContext] Login successful, storing token and user');
        
        // Store authentication data
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(userData));
        
        // Update state
        setUser(userData);
        setIsLoggedIn(true);
        setLoginModalOpen(false);

        return { 
          success: true, 
          user: userData,
          message: response.data.message 
        };
      }

      // API returned non-success status
      console.log('[AuthContext] Login failed - non-success status:', response.data);
      return { 
        success: false, 
        message: response.data.message || 'Login failed' 
      };
    } catch (error) {
      console.error('[AuthContext] Login error:', error);
      
      // Handle API error responses (validation errors, wrong credentials, etc.)
      if (error.response?.data) {
        const apiError = error.response.data;
        console.log('[AuthContext] API error response:', apiError);
        return {
          success: false,
          message: apiError.message || 'Login failed. Please check your credentials.',
          errors: apiError.errors || {}
        };
      }
      
      // Handle network errors or other exceptions
      console.log('[AuthContext] Network or other error:', error.message);
      return {
        success: false,
        message: error.message || 'Network error. Please check your connection.'
      };
    }
  };

  /**
   * Register new customer account
   * @param {Object} userData - { username, email, password, password_confirmation, full_name }
   * @returns {Promise}
   */
  const signup = async (userData) => {
    try {
      const response = await authAPI.register({
        username: userData.username,
        email: userData.email,
        password: userData.password,
        password_confirmation: userData.password_confirmation || userData.password,
        full_name: userData.full_name || userData.name
      });

      if (response.data.success === true) {
        const { token, user: newUser } = response.data.data;
        
        // Store authentication data
        localStorage.setItem('auth_token', token);
        localStorage.setItem('user', JSON.stringify(newUser));
        
        // Update state
        setUser(newUser);
        setIsLoggedIn(true);

        return { 
          success: true, 
          user: newUser,
          message: response.data.message 
        };
      }

      return { success: false, message: 'Registration failed' };
    } catch (error) {
      console.error('Signup error:', error);
      const errorMessage = error.response?.data?.message || 'Registration failed. Please try again.';
      const errors = error.response?.data?.errors || {};
      return { success: false, message: errorMessage, errors };
    }
  };

  /**
   * Logout current user
   * @returns {Promise}
   */
  const logout = async () => {
    try {
      // Call logout API to invalidate token on server
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear local storage and state regardless of API call result
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      setUser(null);
      setIsLoggedIn(false);
    }
  };

  /**
   * Get current user profile from API
   * @returns {Promise}
   */
  const fetchProfile = async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.data.success === true) {
        // data may be { user: {...} } or the user object directly
        const payload = response.data.data || {};
        const userData = payload.user || payload;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, user: userData };
      }
      return { success: false };
    } catch (error) {
      console.error('Fetch profile error:', error);
      return { success: false, message: error.response?.data?.message };
    }
  };

  /**
   * Update user profile
   * @param {Object} data - { full_name, email, password, password_confirmation }
   * @returns {Promise}
   */
  const updateProfile = async (data) => {
    try {
      const response = await authAPI.updateProfile(data);
      if (response.data.success === true) {
        // data may be { user: {...} } or the user object directly
        const payload = response.data.data || {};
        const userData = payload.user || payload;
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(userData));
        return { success: true, user: userData, message: response.data.message };
      }
      return { success: false };
    } catch (error) {
      console.error('Update profile error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      const errors = error.response?.data?.errors || {};
      return { success: false, message: errorMessage, errors };
    }
  };

  /**
   * Check if user has a specific role
   * @param {string} role - 'admin', 'staff', or 'customer'
   * @returns {boolean}
   */
  const hasRole = (role) => {
    return user?.role === role;
  };

  /**
   * Check if user is admin
   * @returns {boolean}
   */
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  /**
   * Check if user is staff
   * @returns {boolean}
   */
  const isStaff = () => {
    return user?.role === 'staff';
  };

  /**
   * Check if user is customer
   * @returns {boolean}
   */
  const isCustomer = () => {
    return user?.role === 'customer';
  };

  /**
   * Check if user has admin or staff privileges
   * @returns {boolean}
   */
  const isAdminOrStaff = () => {
    return user?.role === 'admin' || user?.role === 'staff';
  };

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        user,
        loading,
        loginModalOpen,
        openLoginModal,
        closeLoginModal,
        login,
        signup,
        logout,
        fetchProfile,
        updateProfile,
        hasRole,
        isAdmin,
        isStaff,
        isCustomer,
        isAdminOrStaff,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use AuthContext
 * @returns {Object} AuthContext value
 */
export const useAuth = () => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};


